import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { Exhibitor } from '../types/Exhibitor.model'
import { useMeetingsService } from '../services/meetings.service'
import { getMeetingDateStr, getTodayDateStr } from '../../utils/dateUtils'
import { Meeting } from '../types/Meeting.model'
import { BookMeetingResult } from '../types/BookMeetingResult.model'
import { Message } from '../types/Message.model'


interface DataContextType {
  exhibitors: Exhibitor[],
  exhibitorsMeetingsDates: string[],
  todayDateOption: string | null,
  myMeetings: Meeting[],
  refreshExhibitorsData: () => void,
  bookMeeting: (
    meeting: Meeting, origName?: string, comment?: string
  ) => Promise<boolean>,
  lockMeeting: (meeting: Meeting) => Promise<boolean>,
  unlockMeeting: (meeting: Meeting) => void,
  cancelBookedMeeting: (
    meeting: Meeting
  ) => void,
  message: Message | null,
  cleanMessage: () => void,
  getExhibitorDetails: (exhibitor: Exhibitor) => void,
  isLoaded: boolean,
  logosMap: Record<number, string | null>,
  downloadReport: () => void,
}

const DataContext = createContext<DataContextType | undefined>(undefined)

export const useData = () => {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context;
}



export const DataProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>([]);
  const [exhibitorsMeetingsDates, setExhibitorsMeetingsDates] = useState<string[]>([]);

  const [myMeetings, setMyMeetings] = useState<Meeting[]>([]);

  const meetingsService = useMeetingsService();

  const [message, setMessage] = useState<Message | null>(null);

  const [logosMap, setLogosMap] = useState<Record<number, string | null>>({});

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    setIsLoaded(true);
    meetingsService.getExhibitors().then(exhibitors => {


      setExhibitors(exhibitors);
      exhibitors.forEach(async (ex) => {
        const blob = await getExhibitorLegoFromApi(ex.id);
        const logoUrl = blob ? URL.createObjectURL(blob) : null;
        setLogosMap(prevLogosMap => ({ ...prevLogosMap, [ex.id]: logoUrl }));
      })
    })


    meetingsService.getMyMeetings().then(data => {
      setMyMeetings(data)
    });
  }, [])

  useEffect(() => setIsLoaded(false), [exhibitors, myMeetings]);

  useEffect(() => {
    exhibitors.forEach(exhibitor => {
      if (exhibitor.salesPersons) {
        exhibitor.salesPersons.forEach(salesPerson => {
          if (salesPerson.meetings) {
            const dates = new Set<string>();

            salesPerson.meetings.forEach(meeting => {
              dates.add(getMeetingDateStr(meeting.startTime));
            });

            salesPerson.dates = Array.from(dates);
          }
        });
      }
    });
    const allDates = Array.from(
      new Set(
        exhibitors.flatMap(exhibitor =>
          exhibitor.salesPersons?.flatMap(salesPerson => salesPerson.dates ?? []) ?? []
        )
      )
    );

    setExhibitorsMeetingsDates(allDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()));
  }, [exhibitors]);

  const todayDateOption: string | null = useMemo(() => {
    return exhibitorsMeetingsDates.find(date => date === getTodayDateStr()) ?? null;
  }, [exhibitorsMeetingsDates]);


  const refreshExhibitorsData = () => {
    meetingsService.getExhibitors().then(data => {
      setExhibitors(data)
    });
  }


  const lockMeeting = (meeting: Meeting) => {
    _checkMeetingOverlap(meeting);

    return meetingsService.lockMeeting(meeting.id).then((result: BookMeetingResult) => {
      processBookingResult(result);
      return result.success === true;
    });
  }

  const unlockMeeting = (meeting: Meeting) => {
    setIsLoaded(true);
    return meetingsService.unlockMeeting(meeting.id)
      .finally(() => setIsLoaded(false));
  }

  const bookMeeting = (meeting: Meeting, origName?: string, comment?: string) => {
    setIsLoaded(true);
    return meetingsService.bookMeeting(meeting.id, comment, meeting.hasOverlap).then((result: BookMeetingResult) => {
      processBookingResult(result);

      if (result.success && result.updatedMeeting) {
        const updateMeeting: Meeting = result.updatedMeeting;
        setMyMeetings(prevMeetings => [...prevMeetings, updateMeeting]);

        const salesPersonsNames = updateMeeting.salesPersons
          ?.map(e => e.fullName)
          .join(", ");

        const message: Message = {
          type: 'success', text:
            `Your meeting with ${salesPersonsNames} ${origName && `from ${origName}`} has been confirmed.`,
          closeAction: { label: 'Close', callback: () => setMessage(null), },
          header: 'Meeting booked successfully!'
        };
        setMessage(message);

      }

      return result.success === true;
    }).finally(() => setIsLoaded(false));
  }

  const cancelBookedMeeting = (meeting: Meeting
  ) => {
    setIsLoaded(true);
    meetingsService.cancelBookedMeeting(meeting.id).then((result: BookMeetingResult) => {
      let message: Message | null = null;
      if (!result.success) {
        message = {
          type: 'error',
          text: result.error ?? 'Failed to cancel meeting. Please try again later.',
          closeAction: { label: 'Close', callback: () => setMessage(null), },
          header: 'Cancellation Failed'
        };
      }
      else if (result.updatedMeeting) {
        const updatedMeeting: Meeting = result.updatedMeeting;
        setMyMeetings(prevMeetings => prevMeetings.filter(m => m.id !== updatedMeeting.id));

        updateExhibitorsMeetingAfterMeetingChange(updatedMeeting);

        message = {
          type: 'success',
          text: 'The slot has been made available for other delegates.',
          closeAction: { label: 'Close', callback: () => setMessage(null), },
          header: 'Meeting cancelled successfully!'
        };

      }
      setMessage(message);


    }).finally(() => setIsLoaded(false));

  }

  const cleanMessage = () => {
    setMessage(null);
  }

  const getExhibitorDetails = (exhibitor: Exhibitor) => {
    if (!exhibitor) return;

    if (exhibitor.details) return;

    meetingsService.getExhibitorDetails(exhibitor.id).then(details => {
      exhibitor.details = details ?? 'Error displaying data';

      setExhibitors(prevExhibitors =>
        prevExhibitors.map(exsitExhibitor =>
          exhibitor.id === exsitExhibitor.id ?
            { ...exsitExhibitor, details: exhibitor.details } :
            exsitExhibitor)
      )
    })
  }

  const getExhibitorLegoFromApi = async (id: number): Promise<Blob | null> => {
    if (!id) return null;
    const blob = await meetingsService.getExhibitorLego(id);
    return blob;
  }



  const updateExhibitorsMeetingAfterMeetingChange = (updatedMeeting: Meeting) => {
    if (!updatedMeeting) return;

    handelMeetingUpdate(updatedMeeting)

  }

  const handelMeetingUpdate = (updatedMeeting: Meeting) => {
    setExhibitors(prevExhibitors => {
      return prevExhibitors.map(exhibitor => {
        if (exhibitor.id === updatedMeeting.exhibitorId) {
          const updatedSalesPersons = exhibitor.salesPersons?.map(salesPerson => {
            const exist = salesPerson.meetings?.find(m => m.id === updatedMeeting.id);
            if (exist) exist.status = updatedMeeting.status;
            return salesPerson;
          });
          return { ...exhibitor, salesPersons: updatedSalesPersons };
        }
        return exhibitor;
      });
    });
  }

  const processBookingResult = (result: BookMeetingResult) => {
    if (result.success === false) {
      const message: Message = {
        type: 'error',
        text: result.error ?? 'Failed to book meeting. Please try again later.',
        closeAction: { label: 'Close', callback: () => setMessage(null), },
        actions: [{ label: 'Refresh date', callback: () => { setMessage(null); refreshExhibitorsData() }, className: 'btn-primary' }],
        header: getMessageHeader(result.updatedMeeting?.status)
      };
      setMessage(message);
    }

    if (result.updatedMeeting) {
      updateExhibitorsMeetingAfterMeetingChange(result.updatedMeeting);
    }
  }

  const getMessageHeader = (status: string | null | undefined) => {
    switch (status) {
      case "Booked":
        return "Slot Already Booked";
      case "Closed":
        return "Slot Closed by Exhibitor";
      default:
        return "Booking Failed";
    }
  };

  const downloadReport = () => {
    meetingsService.GetMyMeetingsReport().then((blob: Blob | null) => {
      if(!blob) return;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'My_Meetings_Report.pdf';
      document.body.appendChild(a); 
      a.click();
      a.remove(); 
      window.URL.revokeObjectURL(url);
    });
  }

  const _checkMeetingOverlap = (meeting: Meeting): boolean => {
    const meetingStart = new Date(meeting.startTime).getTime();
    const meetingEnd = new Date(meeting.endTime).getTime();
    for (const myMeeting of myMeetings) {
      const myMeetingStart = new Date(myMeeting.startTime).getTime();
      const myMeetingEnd = new Date(myMeeting.endTime).getTime();
      if (
        (meetingStart < myMeetingEnd) && (meetingEnd > myMeetingStart)
      ) {
        meeting.hasOverlap = true;
        return true;
      }
    }
    meeting.hasOverlap = false;
    return false;
  }

  const value: DataContextType = {
    exhibitors,
    exhibitorsMeetingsDates,
    todayDateOption,
    myMeetings,
    refreshExhibitorsData,
    bookMeeting,
    lockMeeting,
    unlockMeeting,
    cancelBookedMeeting,
    message,
    cleanMessage,
    isLoaded,
    getExhibitorDetails,
    logosMap,
    downloadReport
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}
